import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    InputBase,
    MenuItem,
    Select,
    CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { MyAddRoundedIcon } from "../../Assets/svg/MyIcons";

import { useNavigate } from "react-router-dom";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import CustomSnackbar from "../Common/snackBar";
import { getPermissionData, getUserData } from "../../Actions/common";
import {
    getModulesAction,
    listTutorialsAction,
    getPopularArticlesAction,
} from "../../Actions/KnowledgeBaseAction";
import { KBListItem } from "./KBListItem";
import { ModuleCard } from "./ModuleCard";
import {
    KB_MODULE_SELECT_CLASS,
    KB_PAGE_MENU_PROPS,
} from "./moduleDisplay";
import "./Knowledgebase.css";

const DEFAULT_MODULE_VISIBLE = 5;

const KnowledgeBaseSection = () => {
    const navigate = useNavigate();

    // Permissions
    const userData = localStorage.getItem("userData") ? getUserData() : null;
    const permissionData = localStorage.getItem(
        `${userData?.id}_permissionData`,
    )
        ? getPermissionData(`${userData?.id}_permissionData`)
        : [];
    const hasPermission = permissionData[0]?.role_permissions?.find(
        (m) => m?.module && m?.module?.slug === "knowledge-base",
    );

    const [modules, setModules] = useState([]);
    const [popularArticles, setPopularArticles] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    const [moduleFilter, setModuleFilter] = useState("all");
    const [browseExpanded, setBrowseExpanded] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const showSnack = (message, severity = "success") =>
        setSnackbar({ open: true, message, severity });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [mods, pops] = await Promise.all([
                getModulesAction(),
                getPopularArticlesAction(),
            ]);
            setModules(Array.isArray(mods) ? mods : []);
            setPopularArticles(Array.isArray(pops) ? pops : []);
        } catch (error) {
            showSnack("Failed to load knowledge base data.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Search effect
    useEffect(() => {
        if (!search.trim()) {
            setIsSearching(false);
            setSearchResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setIsSearching(true);
            try {
                const res = await listTutorialsAction({
                    search: search.trim(),
                    moduleSlug: moduleFilter === "all" ? "" : moduleFilter,
                });
                setSearchResults(Array.isArray(res) ? res : res.rows || []);
            } catch (err) {
                console.error(err);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [search, moduleFilter]);

    const browseModules = modules.filter(
        (m) => Number(m.articleCount) > 0,
    );
    const visibleBrowseModules = browseExpanded
        ? browseModules
        : browseModules.slice(0, DEFAULT_MODULE_VISIBLE);

    return (
        <Box id="kt_content_container" className="kb-home">
            <BreadcrumbsComponent breadcrumbs={[{ label: "Knowledge Base" }]} />

            <div className="kb-page-head">
                <div>
                    <h1>Knowledge Base</h1>
                    <p>
                        Search articles or browse modules to find what you need.
                    </p>
                </div>
                {hasPermission?.canCreate && (
                    <button
                        type="button"
                        className="btn btn-outline btn-primary btn-add"
                        onClick={() => navigate("/knowledge-base/new")}
                    >
                        <MyAddRoundedIcon className="me-1 text-white" /> ADD
                    </button>
                )}
            </div>

            <div className="kb-search-panel">
                <div className="kb-search-field">
                    <span className="kb-search-icon" aria-hidden>
                        <SearchIcon />
                    </span>
                    <InputBase
                        className="kb-search-input"
                        placeholder="Search knowledge base..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        inputProps={{ "aria-label": "search-knowledgebase" }}
                    />
                </div>
                <div className="kb-search-module">
                    <Select
                        className={KB_MODULE_SELECT_CLASS}
                        value={moduleFilter}
                        onChange={(e) => setModuleFilter(e.target.value)}
                        MenuProps={KB_PAGE_MENU_PROPS}
                        displayEmpty
                        size="small"
                    >
                        <MenuItem value="all">All Modules</MenuItem>
                        {modules.map((m) => (
                            <MenuItem key={m.slug} value={m.slug}>
                                {m.moduleName}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
            </div>

            {isSearching && (
                <section className="kb-section">
                    <div className="kb-section-head">
                        <h2>Search Results</h2>
                    </div>
                    <div className="kb-results-grid">
                        {searchResults.length > 0 ? (
                            searchResults.map((article) => (
                                <KBListItem
                                    key={article.id}
                                    article={article}
                                    onClick={() =>
                                        navigate(
                                            `/knowledge-base/${article.moduleSlug}/${article.id}`,
                                        )
                                    }
                                />
                            ))
                        ) : (
                            <div className="kb-empty">
                                <p>
                                    No knowledge base found matching "{search}"
                                </p>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {!isSearching && (
                <section className="kb-section">
                    <div className="kb-section-head">
                        <h2>Browse by Module</h2>
                    </div>

                    <div className="kb-module-grid">
                        {loading ? (
                            <div className="kb-loading">
                                <CircularProgress size={28} />
                            </div>
                        ) : visibleBrowseModules.length > 0 ? (
                            visibleBrowseModules.map((m) => (
                                <ModuleCard
                                    key={m.id}
                                    moduleItem={m}
                                    onClick={() =>
                                        navigate(`/knowledge-base/${m.slug}`)
                                    }
                                />
                            ))
                        ) : (
                            <div className="kb-empty">
                                <p>No modules with knowledge base entries yet.</p>
                            </div>
                        )}
                    </div>

                    {browseModules.length > DEFAULT_MODULE_VISIBLE && (
                        <div className="kb-text-link-wrap">
                            <button
                                type="button"
                                className="kb-text-link"
                                onClick={() =>
                                    setBrowseExpanded((p) => !p)
                                }
                            >
                                {browseExpanded
                                    ? "Collapse ↑"
                                    : "View All →"}
                            </button>
                        </div>
                    )}
                </section>
            )}

            {!isSearching && popularArticles.length > 0 && (
                <section className="kb-section">
                    <div className="kb-section-head">
                        <h2>Popular Knowledge Base</h2>
                    </div>
                    <div className="kb-results-grid">
                        {popularArticles.map((article) => (
                            <KBListItem
                                key={article.id}
                                article={article}
                                onClick={() =>
                                    navigate(
                                        `/knowledge-base/${article.moduleSlug}/${article.id}`,
                                    )
                                }
                            />
                        ))}
                    </div>
                    <div className="kb-text-link-wrap">
                        <button
                            type="button"
                            className="kb-text-link"
                            onClick={() => navigate("/knowledge-base/all")}
                        >
                            View All →
                        </button>
                    </div>
                </section>
            )}

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
            />
        </Box>
    );
};

export default KnowledgeBaseSection;
